import {
    FLOW_DEVICE_FIRST,
    RedTariff,
    RedplusTariff,
    SALESCHANNEL_CONSUMER,
    SALESCHANNEL_FAMILY_FRIENDS,
    YoungTariff,
} from '@vfde-sails/constants';
import {
    Capacity,
    Color,
} from '@vfde-sails/glados-v2';
import { createAppSlice } from '../../app/createAppSlice';
import {
    getHashcode,
    startFnFValidation,
} from '@vfde-sails/validation';
import { PayloadAction } from '@reduxjs/toolkit';
import { getHardwareDetailGroup } from '../../api/glados';
import { IAccordionItemProperties } from '@vfde-brix/ws10/accordion';
import { CustomerConfig } from '../../interfaces/state';
import { getIsTradeInEligible } from '../tradeIn/helpers/getTradeIn';

/**
 * Data for initial state
 */
export interface AppState {
    /**
     * Customer configuration
     */
    customerConfig: CustomerConfig;
    /**
     * Skip connect more page
     */
    skipConnectMore: boolean;
    /**
     * Current color
     */
    currentColor: Color['displayLabel'] | null;
    /**
     * Current capacity
     */
    currentCapacity: Capacity['sortValue'] | null;
    /**
     * ActiveAccordionItemId for the technical details accordion.
     * As the accordion is updated on every atomic change any opened item would be closed.
     * This property is used to store the ID of the last opened item and re-open it on update.
     */
    activeAccordionItemId: string | null;
}

/**
 * Initial state
 */
const initialState: AppState = {
    customerConfig: {
        isTauschbonusEligible: false,
        salesChannel: SALESCHANNEL_CONSUMER,
        subscriptionId: null,
        deviceId: null,
        atomicId: null,
        isGigakombi: false,
        isTauschbonus: false,
        isTradeIn: false,
        insurance: null,
        isHardwareOnly: false,
        isRestlaufzeit: false,
        isGigakombiEligible: false,
        flowType: FLOW_DEVICE_FIRST,
    },
    skipConnectMore: false,
    currentColor: null,
    currentCapacity: null,
    activeAccordionItemId: null,
};

/**
 * SetInitialStateArgs
 */
export interface SetInitialStateArgs {
    /* eslint-disable jsdoc/require-jsdoc */
    customerConfig: CustomerConfig;
    /* eslint-enable jsdoc/require-jsdoc */
}

const appSlice = createAppSlice({
    name: 'DeviceDetails',
    initialState,
    reducers: create => {
        const createAsyncThunk = create.asyncThunk.withTypes<{
            rejectValue: string;
        }>();

        return {
            setInitialState: createAsyncThunk<SetInitialStateArgs, SetInitialStateArgs>(
                async (arg, thunkApi) => {
                    if (arg.customerConfig.salesChannel === SALESCHANNEL_FAMILY_FRIENDS) {
                        const hashcode = getHashcode();
                        const resultAction = await thunkApi.dispatch(startFnFValidation(hashcode));
                        const validationHasFailed = startFnFValidation.rejected.match(resultAction);

                        if (validationHasFailed) {
                            return thunkApi.rejectWithValue(resultAction.payload!);
                        }
                    }

                    return arg;
                },
                {
                    fulfilled: (state, action) => {
                        const {
                            salesChannel = SALESCHANNEL_CONSUMER,
                            subscriptionId = null,
                            deviceId = null,
                            atomicId = null,
                            isGigakombi = false,
                            isTauschbonus = false,
                            isTradeIn = false,
                            insurance = null,
                            isHardwareOnly = false,
                            isRestlaufzeit = false,
                            isGigakombiEligible = false,
                            isTauschbonusEligible = false,
                            flowType = FLOW_DEVICE_FIRST,
                        } = action.payload.customerConfig;

                        const customerConfig = {
                            salesChannel,
                            subscriptionId,
                            deviceId,
                            atomicId,
                            isGigakombi,
                            isTauschbonus,
                            isTradeIn,
                            insurance,
                            isHardwareOnly,
                            isRestlaufzeit,
                            isGigakombiEligible,
                            isTauschbonusEligible,
                            flowType,
                        };

                        state.customerConfig = customerConfig;
                    },
                },
            ),
            checkTradeInEligibility: createAsyncThunk<CustomerConfig, CustomerConfig>(
                (arg, thunkApi) => {
                    const isTradeInEligible = getIsTradeInEligible(arg.salesChannel);

                    if (!isTradeInEligible) {
                        return thunkApi.rejectWithValue('TradeIn not eligible');
                    }

                    return arg;
                },
            ),
            changeColor: create.reducer((state, action: PayloadAction<Color['displayLabel']>) => {
                state.currentColor = action.payload || null;
            }),
            changeCapacity: create.reducer((state, action: PayloadAction<Capacity['sortValue']>) => {
                state.currentCapacity = action.payload || null;
            }),
            setAtomicId: create.reducer((state, action: PayloadAction<string>) => {
                state.customerConfig.atomicId = action.payload;
            }),
            setSubscriptionId: create.reducer<RedTariff | YoungTariff | RedplusTariff | null>((state, action) => {
                state.customerConfig.subscriptionId = action.payload;
            }),
            toggleHardwareOnly: create.reducer(state => {
                state.customerConfig.isHardwareOnly = !state.customerConfig.isHardwareOnly;
            }),
            setGigakombi: create.reducer((state, action: PayloadAction<boolean>) => {
                state.customerConfig.isGigakombi = action.payload;
            }),
            skipConnectMore: create.reducer((state, action: PayloadAction<boolean>) => {
                state.skipConnectMore = action.payload || false;
            }),
            setTradeIn: create.reducer((state, action: PayloadAction<boolean>) => {
                state.customerConfig.isTradeIn = action.payload;
                state.customerConfig.isTauschbonus = state.customerConfig.isTauschbonusEligible && state.customerConfig.isTradeIn;
            }),
            goToBasket: create.reducer<boolean | undefined>((state, action) => {
                if (action.payload !== undefined) {
                    state.customerConfig.isGigakombi = action.payload;
                }
            }),
            updateStorage: create.reducer(state => state),
            toggleAccordionItem: create.preparedReducer(
                (accordionItemProperties: IAccordionItemProperties) => ({
                    payload: {
                        stdId: accordionItemProperties.stdId,
                        stdHeadline: accordionItemProperties.stdHeadline,
                        optOpen: accordionItemProperties.optOpen,
                    },
                }),
                (state, action) => {
                    state.activeAccordionItemId = action.payload.optOpen ? action.payload.stdId : null;
                },
            ),
        };
    },
    selectors: {
        selectCustomerConfig: sliceState => sliceState.customerConfig,
        selectCurrentColor: sliceState => sliceState.currentColor,
        selectCurrentCapacity: sliceState => sliceState.currentCapacity,
        selectSkipConnectMore: sliceState => sliceState.skipConnectMore,
        selectActiveAccordionItemId: sliceState => sliceState.activeAccordionItemId,

    },
    extraReducers: builder => {
        builder
            .addMatcher(
                getHardwareDetailGroup.matchFulfilled,
                (state, action) => {
                    const { atomics } = action.payload;

                    if (
                        !state.customerConfig.atomicId
                        || !atomics.some(atomic => atomic.atomicId === state.customerConfig.atomicId)
                    ) {
                        // if user has no customerConfig or no atomicId or any invalid atomicId
                        // find the default atomic and use its hardwareId
                        const defaultAtomic = atomics.find(atomic => atomic.isDefault);
                        state.customerConfig.atomicId = defaultAtomic?.atomicId || null;
                    }

                    const currentAtomic = atomics.find(atomic => atomic.atomicId === state.customerConfig.atomicId);

                    state.currentColor = currentAtomic?.color.displayLabel || null;
                    state.currentCapacity = currentAtomic?.capacity.sortValue || null;
                },
            )
        ;
    },
});

export default appSlice;

export const {
    setInitialState,
    skipConnectMore,
    goToBasket,
    updateStorage,
    changeColor,
    changeCapacity,
    setAtomicId,
    toggleHardwareOnly,
    setGigakombi,
    toggleAccordionItem,
    checkTradeInEligibility,
    setTradeIn,
    setSubscriptionId,
} = appSlice.actions;

export const {
    selectCustomerConfig,
    selectCurrentColor,
    selectCurrentCapacity,
    selectSkipConnectMore,
    selectActiveAccordionItemId,
} = appSlice.selectors;
